/**
 * Number, duration, money, date and change formatting for the editorial
 * pages, so a figure reads the same on every page. Each page keeps its own
 * headline and dek templates in its story.ts; the small helpers live here.
 *
 * Missing or non-finite values format as an en dash.
 */

const DASH = "–";

function missing(v: number | null | undefined): v is null | undefined {
  return v === null || v === undefined || !Number.isFinite(v);
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

function round1Text(n: number): string {
  const r = round1(n);
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/* ---------------- Numbers ---------------- */

/**
 * Thousands separators, at most one decimal, and no trailing ".0":
 * 1234 -> "1,234", 19.44 -> "19.4", 3.0 -> "3". `digits` fixes the decimals.
 */
export function fmtNum(v: number | null | undefined, digits = 0): string {
  if (missing(v)) return DASH;
  const r = digits > 0 ? v.toFixed(digits) : String(round1(v));
  const [int, frac] = r.split(".");
  const withCommas = (int ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac && frac !== "0" ? `${withCommas}.${frac}` : withCommas;
}

/** Exactly `digits` decimals with separators: (19.44) -> "19", (19.44, 1) -> "19.4". */
export function fmtFixed(v: number | null | undefined, digits = 0): string {
  if (missing(v)) return DASH;
  return v.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Whole numbers only, with thousands separators. */
export function fmtInt(v: number | null | undefined): string {
  if (missing(v)) return DASH;
  return Math.round(v).toLocaleString("en-US");
}

export function fmtPct(v: number | null | undefined): string {
  if (missing(v)) return DASH;
  return `${Math.round(v)}%`;
}

/* ---------------- Durations ---------------- */

/** Hours as "45m", "6.2h", "3.1d". Zero or less reads "0h". */
export function fmtHours(h: number | null | undefined): string {
  if (missing(h)) return DASH;
  if (h <= 0) return "0h";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${round1Text(h)}h`;
  return `${round1Text(h / 24)}d`;
}

/** Hours with one fixed decimal and weeks past two weeks: "45m", "19.0h", "3.2d", "2.1w". */
export function fmtHoursFixed(h: number | null | undefined): string {
  if (missing(h)) return DASH;
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  const d = h / 24;
  if (d < 14) return `${d.toFixed(1)}d`;
  return `${(d / 7).toFixed(1)}w`;
}

/* ---------------- Money ---------------- */

/** Dollars: cents under $100, whole dollars above. */
export function fmtUsd(v: number | null | undefined): string {
  if (missing(v)) return DASH;
  const abs = Math.abs(v);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: abs >= 100 ? 0 : 2,
    maximumFractionDigits: abs >= 100 ? 0 : 2,
  });
  return `${v < 0 ? "−" : ""}$${s}`;
}

/** Whole dollars, for totals and ledger cells. */
export function fmtUsdWhole(v: number | null | undefined): string {
  if (missing(v)) return DASH;
  return `${v < 0 ? "−" : ""}$${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
}

/** Compact dollars for axes and tight cells: $1.2k, $34k. */
export function fmtUsdShort(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${round1Text(v / 1_000_000)}M`;
  if (abs >= 10_000) return `$${Math.round(v / 1000)}k`;
  if (abs >= 1000) return `$${round1Text(v / 1000)}k`;
  return `$${Math.round(v)}`;
}

/* ---------------- Dates ---------------- */

/** "Oct 6" from an ISO date or yyyy-mm-dd, without a timezone shift. */
export function fmtDay(date: string): string {
  const d = new Date(date.length === 10 ? `${date}T12:00:00` : date);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Oct 6, 2026". */
export function fmtDate(date: string): string {
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** "Mar 2026" from a "2026-03" month key. */
export function fmtMonth(month: string): string {
  const [year, m] = month.split("-");
  if (!year || !m) return month;
  return new Date(Number(year), Number(m) - 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

/* ---------------- Periods ---------------- */

/**
 * The period as it reads mid-sentence: "Last 30 days" -> "the last 30 days".
 * Older calendar labels still work: "October to date" -> "October".
 */
export function periodName(label: string): string {
  if (/^last \d+ days$/i.test(label.trim())) return `the ${label.trim().toLowerCase()}`;
  return label.replace(/\s+to date$/i, "").trim();
}

/**
 * The prior window as it reads mid-sentence: "the prior 30 days" stays as is.
 * Older calendar labels: "September 1 to 23" -> "September"; "Q3 2026, same 84 days" -> "Q3 2026".
 */
export function priorName(prevLabel: string): string {
  const head = prevLabel.split(",")[0]?.trim() ?? prevLabel;
  return head.replace(/\s+1 to \d+$/, "");
}

/** "in the last 30 days" or "in October"; any other label as is. */
export function inPeriod(label: string): string {
  const name = periodName(label);
  return /^(the last \d+ days|Q\d|\d{4}|January|February|March|April|May|June|July|August|September|October|November|December)/.test(
    name,
  )
    ? `in ${name}`
    : name;
}

/* ---------------- Words ---------------- */

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];

/** Newspaper style: spell out one to nine. */
export function countWord(n: number): string {
  return n >= 0 && n < WORDS.length ? WORDS[n]! : fmtNum(n);
}

/** The count and its noun: "1 person", "3 people". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${fmtNum(n)} ${pluralWord(n, one, many)}`;
}

/** Only the noun, for when the count is set apart: "person", "people". */
export function pluralWord(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ---------------- Change ---------------- */

export type Better = "up" | "down" | "neutral";

export interface Delta {
  /** Absolute difference, current minus prior. */
  diff: number;
  /** Percent change, or null when the prior value was zero or missing. */
  pct: number | null;
  arrow: "▲" | "▼" | "";
  /** "+18%", "-3", "new", "0" */
  text: string;
  direction: "up" | "down" | "flat";
  /** Whether the move is good, bad or neither, given which way is better. */
  tone: "good" | "bad" | "neutral";
}

/** Percent change, rounded. Null when there's no base to compare against. */
export function pctChange(
  current: number | null | undefined,
  prior: number | null | undefined,
): number | null {
  if (current === null || current === undefined) return null;
  if (prior === null || prior === undefined || prior === 0) return null;
  return Math.round(((current - prior) / prior) * 100);
}

/**
 * How to show a change. Percent once the base is big enough to mean
 * something (10+), the raw difference below that: going from 2 PRs to 3 is
 * "+1", not "+50%". A percent move under `flatBand` reads as flat.
 */
export function delta(
  current: number | null | undefined,
  prior: number | null | undefined,
  better: Better = "up",
  opts: { flatBand?: number; minBaseForPct?: number; unit?: string } = {},
): Delta {
  const flatBand = opts.flatBand ?? 2;
  const minBase = opts.minBaseForPct ?? 10;
  const unit = opts.unit ?? "";
  const cur = current ?? 0;
  const prev = prior ?? 0;
  const diff = round1(cur - prev);
  const pct = pctChange(current, prior);

  let direction: Delta["direction"] = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  let text: string;
  if (prior === null || prior === undefined || (prev === 0 && cur > 0)) {
    text = cur > 0 ? "new" : "0";
  } else if (pct !== null && Math.abs(prev) >= minBase) {
    if (Math.abs(pct) < flatBand) direction = "flat";
    text = direction === "flat" ? "0%" : `${pct > 0 ? "+" : ""}${pct}%`;
  } else {
    text = diff === 0 ? "0" : `${diff > 0 ? "+" : ""}${fmtNum(diff)}${unit}`;
  }

  const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "";
  const tone: Delta["tone"] =
    better === "neutral" || direction === "flat"
      ? "neutral"
      : (direction === "up") === (better === "up")
        ? "good"
        : "bad";
  return { diff, pct, arrow, text, direction, tone };
}

/**
 * Ledger change text and arrow, from `delta`: "+18%" ▲, "-2" ▼, "new" ▲ or
 * "no change". Empty when either value is missing. Durations pass
 * `minBaseForPct: 0` to read as a percent at any size.
 */
export function changeText(
  current: number | null | undefined,
  prior: number | null | undefined,
  opts: { minBaseForPct?: number; unit?: string; flatBand?: number; arrowOnNew?: boolean } = {},
): { change?: string; arrow?: string } {
  if (current === null || current === undefined || prior === null || prior === undefined) return {};
  const d = delta(current, prior, "up", opts);
  if (d.text === "0" || d.text === "0%") return { change: "no change", arrow: "" };
  if (d.text === "new" && opts.arrowOnNew === false) return { change: "new", arrow: "" };
  return { change: d.text, arrow: d.arrow };
}

/* ---------------- Lists ---------------- */

/** "a", "a and b", "a, b, and c". */
export function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
